/**
 * SkillsGallery Component
 * 
 * A gallery component for selecting skills.
 * Displays all user skills and returns the selected skill's ID.
 * 
 * Features:
 * - Display all user skills with ratings
 * - Click to select a skill
 * - Returns skill ID on selection
 * - Loading and error states
 * - Responsive grid layout
 * - Star rating display
 * 
 * Props:
 *   - userId: Current user's unique ID
 *   - onSelect: Callback function that receives the skill ID
 *   - onClose: Optional callback to close the gallery
 */

import React, { useEffect, useState } from 'react';
import { loadUserSkills } from './utilities/skillsUtils.js';
import FixedMenu from '../../Common/Menus/FixedMenu.jsx';
import IconDisplay from '../../Common/Icons/IconDisplay.jsx';

const EMPTY_SELECTED_IDS = Object.freeze([]);

const SkillsGallery = ({ userId, onSelect, setExpanded, multiSelect = false, initialSelectedIds = EMPTY_SELECTED_IDS }) => {
  const [skills, setSkills] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedSkillIds, setSelectedSkillIds] = useState([]);

  /**
   * Load skills from Firebase
   */
  const loadSkills = async () => {
    setLoading(true);
    setError(null);

    try {
      const userSkills = await loadUserSkills(userId);
      setSkills(userSkills || []);
    } catch (err) {
      console.error('Error fetching skills:', err);
      setError('Failed to load skills');
      setSkills([]);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Load skills on component mount or when userId changes
   */
  useEffect(() => {
    if (userId) {
      loadSkills();
    }
  }, [userId]);

  useEffect(() => {
    if (!multiSelect) {
      setSelectedSkillIds((previous) => (previous.length === 0 ? previous : []));
      return;
    }

    if (Array.isArray(initialSelectedIds)) {
      setSelectedSkillIds((previous) => {
        const sameLength = previous.length === initialSelectedIds.length;
        const sameValues = sameLength && previous.every((id, index) => id === initialSelectedIds[index]);
        return sameValues ? previous : initialSelectedIds;
      });
    }
  }, [multiSelect, initialSelectedIds]);

  /**
   * Handle skill selection
   * Returns the skill ID to the parent component
   */
  const handleSelectSkill = (skillId) => {
    if (multiSelect) {
      setSelectedSkillIds((previous) => {
        if (previous.includes(skillId)) {
          return previous.filter((id) => id !== skillId);
        }
        return [...previous, skillId];
      });
      return;
    }

    onSelect(skillId);
    if (setExpanded) {
      setExpanded(false);
    }
  };

  const handleApplySelection = () => {
    onSelect(selectedSkillIds);
    if (setExpanded) {
      setExpanded(false);
    }
  };

  return (
    <FixedMenu title={multiSelect ? 'Select Skills' : 'Select Skill'} iconClass="bi-stars" expanded={true} setExpanded={setExpanded}>
          {multiSelect ? (
            <div className="skills-selection-toolbar">
              <span className="skills-selection-count" aria-live="polite">
                {selectedSkillIds.length} skill{selectedSkillIds.length === 1 ? '' : 's'} selected
              </span>
              <button
                className="btn btn-sm btn-primary"
                type="button"
                onClick={handleApplySelection}
                disabled={selectedSkillIds.length === 0}
              >
                Confirm selected
              </button>
            </div>
          ) : null}

          {loading ? (
            <div className="gallery-loading text-center py-5">
              <div className="spinner-border spinner-border-sm text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p className="mt-2 text-muted">Loading skills...</p>
            </div>
          ) : error ? (
            <div className="alert alert-danger mb-0">
              <small>{error}</small>
            </div>
          ) : skills.length === 0 ? (
            <div className="gallery-empty text-center py-5">
              <i className="bi bi-stars d-block fs-3 text-muted mb-2" aria-hidden="true"></i>
              <p className="text-muted mb-1">No skills found</p>
              <small className="text-muted">Create a skill first, then return to select it.</small>
            </div>
          ) : (
            <>
              <div className="skills-selection-summary">
                <span>{skills.length} skill{skills.length === 1 ? '' : 's'} available</span>
                <span>Select {multiSelect ? 'one or more to add' : 'a skill to add'}</span>
              </div>
              <div className="row g-3 skills-selection-grid">
                {skills.map((skill) => {
                  const selected = multiSelect && selectedSkillIds.includes(skill.id);
                  const rating = Math.max(0, Math.min(5, Number(skill.rating) || 0));

                  return (
                  <div key={skill.id} className="col-12 col-sm-6 col-xl-4">
                    <article className={`card skills-selection-card ${selected ? 'is-selected' : ''}`}>
                      <div className="card-body">
                        <div className="skills-selection-card-heading">
                          <span className="skills-selection-icon">
                            <IconDisplay icon={skill.icon} userId={userId} label={skill.name} size={44} />
                          </span>
                          <div className="min-w-0 flex-grow-1">
                            <h4 title={skill.name}>{skill.name}</h4>
                            <div className="skills-selection-rating" aria-label={`${rating} out of 5 stars`}>
                              {Array.from({ length: 5 }, (_, index) => (
                                <i key={index} className={`bi ${index < rating ? 'bi-star-fill' : 'bi-star'}`} aria-hidden="true"></i>
                              ))}
                            </div>
                          </div>
                        </div>
                        <p title={skill.description}>{skill.description || 'No description provided.'}</p>
                      </div>
                      <div className="card-footer">
                        <button
                          type="button"
                          className={`btn w-100 ${selected ? 'btn-primary' : 'btn-outline-secondary'}`}
                          onClick={() => handleSelectSkill(skill.id)}
                          aria-label={`${selected ? 'Deselect' : 'Select'} ${skill.name}`}
                          aria-pressed={selected}
                        >
                          <i className={`bi ${selected ? 'bi-check-lg' : 'bi-plus-lg'} me-2`} aria-hidden="true"></i>
                          {selected ? 'Selected' : 'Select'}
                        </button>
                      </div>
                    </article>
                  </div>
                  );
                })}
              </div>
            </>
          )}
      </FixedMenu>
  );
};

export default SkillsGallery;
